import type { $Fetch } from 'ofetch';
import type { CpaGateway, ListCpaCiclosParams } from '@/application/port/out/CpaGateway';
import type {
  CpaCiclo, CpaDimensao, CpaRelatorioSintetico,
  CriarCpaCicloCommand, ProrrogarCpaCicloCommand,
} from '@/domain/model/Cpa';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway do admin de CPA (acadadmin slice).
 * Root: `/api/v1/cpa/ciclos`. Authorities `acadadmin:cpa:read|manage|ver_relatorio`.
 *
 * @since G.5.13
 */
export class HttpCpaGateway implements CpaGateway {
  private readonly base = '/api/v1/cpa/ciclos';
  constructor(private readonly client: $Fetch) {}

  async list(params: ListCpaCiclosParams): Promise<Page<CpaCiclo>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.status) query.status = params.status;
    if (params.ano != null) query.ano = params.ano;
    const res = await this.client<ApiResponse<Page<CpaCiclo>>>(
      this.base, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<CpaCiclo> {
    const res = await this.client<ApiResponse<CpaCiclo>>(
      `${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async criar(cmd: CriarCpaCicloCommand): Promise<CpaCiclo> {
    const res = await this.client<ApiResponse<CpaCiclo>>(
      this.base, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  private async transicao(id: string, acao: string): Promise<CpaCiclo> {
    const res = await this.client<ApiResponse<CpaCiclo>>(
      `${this.base}/${encodeURIComponent(id)}/${acao}`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  agendar(id: string): Promise<CpaCiclo> { return this.transicao(id, 'agendar'); }
  fechar(id: string): Promise<CpaCiclo> { return this.transicao(id, 'fechar'); }
  arquivar(id: string): Promise<CpaCiclo> { return this.transicao(id, 'arquivar'); }

  async prorrogar(id: string, cmd: ProrrogarCpaCicloCommand): Promise<CpaCiclo> {
    const res = await this.client<ApiResponse<CpaCiclo>>(
      `${this.base}/${encodeURIComponent(id)}/prorrogar`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async relatorioSintetico(id: string): Promise<CpaRelatorioSintetico> {
    const res = await this.client<ApiResponse<CpaRelatorioSintetico>>(
      `${this.base}/${encodeURIComponent(id)}/relatorio/sintetico`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async listDimensoes(): Promise<CpaDimensao[]> {
    const res = await this.client<ApiResponse<CpaDimensao[]>>(
      `${this.base}/dimensoes`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }
}
